import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as RuleTypesActions from '../actions/rule-types-actions';
import * as UiActions from '../actions/ui-actions';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { OdataService } from '@app/Services/o-data.service';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { RuleTypeData } from '@app/Shared/interfaces/iRuleTypes';

@Injectable()
export class RuleTypesEffects {
    constructor(private actions$: Actions, private odataService: OdataService, private store: Store<AppState>, private alertService: AlertService) {}

    /**
     * Map API field names to component field names
     * API: RuleType, RuleTypeKey, CreateModifyTimestamp
     * Component: ruleType, ruleTypeKey, createModifyTimestamp
     */
    private mapApiToComponent(apiRuleType: any): RuleTypeData {
        return {
            DataFieldRuleTypeKey: apiRuleType.DataFieldRuleTypeKey,
            DataFieldRuleType: apiRuleType.DataFieldRuleType,
            DataFieldRuleTypeTs: apiRuleType.DataFieldRuleTypeTs ? new Date(apiRuleType.DataFieldRuleTypeTs) : apiRuleType.DataFieldRuleTypeTs,
        };
    }

    /**
     * Map component field names to API field names
     */
    private mapComponentToApi(componentRuleType: RuleTypeData): any {
        const apiData: any = {
            DataFieldRuleType: componentRuleType.DataFieldRuleType,
        };

        // Only include RuleTypeKey if it exists (for updates)
        if (componentRuleType.DataFieldRuleTypeKey) {
            apiData.DataFieldRuleTypeKey = componentRuleType.DataFieldRuleTypeKey;
        }

        return apiData;
    }
    loadRuleTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleTypesActions.loadRuleTypes),
            switchMap(() => {
                this.store.dispatch(UiActions.startLoading());
                return this.odataService.getRuleTypes().pipe(
                    map((ruleTypes: any[]) => {
                        const mappedRuleTypes = ruleTypes.map((ruleType: any) => this.mapApiToComponent(ruleType));
                        return RuleTypesActions.loadRuleTypesSuccess({ ruleTypes: mappedRuleTypes });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RuleTypesActions.loadRuleTypesFailure({ error: error.message || 'Failed to load rule types' }));
                    })
                );
            })
        )
    );

    createRuleType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleTypesActions.createRuleType),
            switchMap(({ ruleTypeData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(ruleTypeData);
                return this.odataService.createRuleType(apiData).pipe(
                    map((response: any) => {
                        const createdRuleType = this.mapApiToComponent(response);
                        if (response.DataFieldRuleTypeKey) {
                            createdRuleType.DataFieldRuleTypeKey = response.DataFieldRuleTypeKey;
                        }
                        return RuleTypesActions.createRuleTypeSuccess({ ruleType: createdRuleType });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RuleTypesActions.createRuleTypeFailure({ error: error.message || 'Failed to create rule type' }));
                    })
                );
            })
        )
    );
    createRuleTypeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.createRuleTypeSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule Type created successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    createRuleTypeFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.createRuleTypeFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to create rule type: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );

    updateRuleType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleTypesActions.updateRuleType),
            switchMap(({ key, ruleTypeData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(ruleTypeData);
                return this.odataService.updateRuleType(key, apiData).pipe(
                    map((response: any) => {
                        const updatedRuleType = this.mapApiToComponent(response);
                        updatedRuleType.DataFieldRuleTypeKey = key;
                        return RuleTypesActions.updateRuleTypeSuccess({ ruleType: updatedRuleType });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RuleTypesActions.updateRuleTypeFailure({ error: error.message || 'Failed to update rule type' }));
                    })
                );
            })
        )
    );
    updateRuleTypeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.updateRuleTypeSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule Type updated successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    updateRuleTypeFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.updateRuleTypeFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to update rule type: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );

    deleteRuleType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleTypesActions.deleteRuleType),
            switchMap(({ key }) => {
                this.store.dispatch(UiActions.startLoading());
                // TODO: Implement deleteRuleType method in OdataService when DELETE endpoint is available
                this.store.dispatch(UiActions.stopLoading());
                return of(RuleTypesActions.deleteRuleTypeSuccess({ key }));
            })
        )
    );
    deleteRuleTypeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.deleteRuleTypeSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule Type deleted successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    deleteRuleTypeFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RuleTypesActions.deleteRuleTypeFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to delete rule type: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );
}
